import React, { useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  FaAddressCard,
  FaEnvelope,
  FaPhoneAlt,
  FaShareAlt,
  FaUser,
  FaBookOpen,
} from "react-icons/fa";
import { ActionButton, Field, inputClass } from "../components/Lds";
import DutchAddressFields from "../../../components/DutchAddressFields";
import type { TeacherFormValues } from "../types";
import type { Subject } from "../types";
import { listSubjects } from "../api";

interface TeacherFormProps {
  values: TeacherFormValues;
  onChange: (values: TeacherFormValues) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  submitting: boolean;
  submitLabel: string;
  allowSharing?: boolean;
}

type TextFieldName = keyof Pick<
  TeacherFormValues,
  | "firstName"
  | "lastName"
  | "address"
  | "zipCode"
  | "city"
  | "phone"
  | "email"
  | "dateBirth"
>;

interface TextFieldDefinition {
  name: TextFieldName;
  key: string;
  defaultValue: string;
  type?: React.HTMLInputTypeAttribute;
  required?: boolean;
  autoComplete?: string;
}

const personalFields: TextFieldDefinition[] = [
  {
    name: "firstName",
    key: "common.given_name",
    defaultValue: "Given name",
    required: true,
    autoComplete: "given-name",
  },
  {
    name: "lastName",
    key: "common.family_name",
    defaultValue: "Family name",
    required: true,
    autoComplete: "family-name",
  },
  {
    name: "dateBirth",
    key: "children.common.date_of_birth",
    defaultValue: "Date of birth",
    type: "date",
    autoComplete: "bday",
  },
];

const contactFields: TextFieldDefinition[] = [
  {
    name: "email",
    key: "family.common.email",
    defaultValue: "Email",
    type: "email",
    autoComplete: "email",
  },
  {
    name: "phone",
    key: "family.common.phone",
    defaultValue: "Phone",
    type: "tel",
    autoComplete: "tel",
  },
];

const sharingFields: Array<{
  name: keyof Pick<
    TeacherFormValues,
    "sharePhone" | "shareEmail" | "shareAddress"
  >;
  key: string;
  defaultValue: string;
  descriptionKey: string;
  descriptionDefault: string;
  icon: React.ReactNode;
}> = [
  {
    name: "sharePhone",
    key: "teachers.edit.share_phone",
    defaultValue: "Share phone number",
    descriptionKey: "teachers.edit.share_phone_description",
    descriptionDefault: "Show the phone number in the school directory.",
    icon: <FaPhoneAlt aria-hidden="true" />,
  },
  {
    name: "shareEmail",
    key: "teachers.edit.share_email",
    defaultValue: "Share email",
    descriptionKey: "teachers.edit.share_email_description",
    descriptionDefault: "Show the email address in the school directory.",
    icon: <FaEnvelope aria-hidden="true" />,
  },
  {
    name: "shareAddress",
    key: "teachers.edit.share_address",
    defaultValue: "Share address",
    descriptionKey: "teachers.edit.share_address_description",
    descriptionDefault: "Show the address in the school directory.",
    icon: <FaAddressCard aria-hidden="true" />,
  },
];

const sectionClass =
  "rounded-[24px] border border-slate-200/80 bg-white/95 p-5 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-6";

const sectionTitleClass = "text-base font-bold text-slate-950 dark:text-white";
const sectionDescriptionClass =
  "mt-1 text-sm text-slate-500 dark:text-slate-400";

const renderFields = (
  fields: TextFieldDefinition[],
  values: TeacherFormValues,
  setValue: (name: keyof TeacherFormValues, value: string | boolean) => void,
  t: TFunction,
) =>
  fields.map((field) => (
    <Field
      key={field.name}
      label={t(field.key, { defaultValue: field.defaultValue })}
    >
      <input
        type={field.type ?? "text"}
        name={field.name}
        value={String(values[field.name] ?? "")}
        onChange={(event) => setValue(field.name, event.target.value)}
        required={field.required}
        autoComplete={field.autoComplete}
        className={inputClass}
      />
    </Field>
  ));

const TeacherForm = ({
  values,
  onChange,
  onSubmit,
  onCancel,
  submitting,
  submitLabel,
  allowSharing = false,
}: TeacherFormProps) => {
  const { t } = useTranslation();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const latestValues = useRef(values);

  useEffect(() => {
    latestValues.current = values;
  }, [values]);

  useEffect(() => {
    void listSubjects().then((items) => setSubjects(items.filter((item) => item.active))).catch(() => setSubjects([]));
  }, []);

  const setValue = (name: keyof TeacherFormValues, value: string | boolean) => {
    const next = { ...latestValues.current, [name]: value };
    latestValues.current = next;
    onChange(next);
  };

  const toggleCapability = (capability: string) => {
    const next = values.capabilities.includes(capability)
      ? values.capabilities.filter((item) => item !== capability)
      : [...values.capabilities, capability];
    onChange({ ...values, capabilities: next, subjectIds: capability === "SUBJECT_TEACHER" || next.includes("SUBJECT_TEACHER") ? values.subjectIds : [] });
  };

  const toggleSubject = (subjectId: string) => {
    onChange({
      ...values,
      subjectIds: values.subjectIds.includes(subjectId)
        ? values.subjectIds.filter((id) => id !== subjectId)
        : [...values.subjectIds, subjectId],
    });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <section className={sectionClass}>
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-300">
            <FaUser aria-hidden="true" />
          </div>
          <div>
            <h2 className={sectionTitleClass}>
              {t("teachers.form.personal_details", {
                defaultValue: "Personal details",
              })}
            </h2>
            <p className={sectionDescriptionClass}>
              {t("teachers.form.personal_details_description", {
                defaultValue: "Enter the teacher's personal information.",
              })}
            </p>
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          {renderFields(personalFields, values, setValue, t)}
        </div>
      </section>

      <section className={sectionClass}>
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 dark:bg-sky-400/10 dark:text-sky-300">
            <FaEnvelope aria-hidden="true" />
          </div>
          <div>
            <h2 className={sectionTitleClass}>
              {t("teachers.form.contact_details", {
                defaultValue: "Contact details",
              })}
            </h2>
            <p className={sectionDescriptionClass}>
              {t("teachers.form.contact_details_description", {
                defaultValue: "Add the contact details used by the school.",
              })}
            </p>
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          {renderFields(contactFields, values, setValue, t)}
        </div>
      </section>

      <section className={sectionClass}>
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-400/10 dark:text-amber-300">
            <FaAddressCard aria-hidden="true" />
          </div>
          <div>
            <h2 className={sectionTitleClass}>
              {t("teachers.form.address_details", {
                defaultValue: "Address details",
              })}
            </h2>
            <p className={sectionDescriptionClass}>
              {t("teachers.form.address_details_description", {
                defaultValue: "Enter the teacher's postal address.",
              })}
            </p>
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <DutchAddressFields
            address={values.address}
            zipCode={values.zipCode}
            city={values.city}
            onChange={(field, value) => setValue(field, value)}
            onAddressFound={(found) => onChange({ ...values, ...found })}
            t={t}
            inputClassName={inputClass}
            labelClassName="block text-sm font-semibold text-slate-700 dark:text-slate-200"
            addressClassName="sm:col-span-2"
          />
        </div>
      </section>

      <section className={sectionClass}>
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-300">
            <FaBookOpen aria-hidden="true" />
          </div>
          <div>
            <h2 className={sectionTitleClass}>{t("teachers.form.capabilities", { defaultValue: "Roles and deployment" })}</h2>
            <p className={sectionDescriptionClass}>{t("teachers.form.capabilities_description", { defaultValue: "Select all roles this staff member can fulfil." })}</p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["CLASS_TEACHER", "Class teacher"],
            ["MENTOR", "Mentor"],
            ["SUBJECT_TEACHER", "Subject teacher"],
            ["ASSISTANT", "Teaching assistant"],
          ].map(([value, label]) => (
            <label key={value} className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950/40">
              <input type="checkbox" checked={values.capabilities.includes(value)} onChange={() => toggleCapability(value)} />
              <span className="text-sm font-semibold text-slate-900 dark:text-white">{t(`teachers.capabilities.${value.toLowerCase()}`, { defaultValue: label })}</span>
            </label>
          ))}
        </div>
        {values.capabilities.includes("SUBJECT_TEACHER") ? (
          <div className="mt-5">
            <p className="mb-3 text-sm font-semibold text-slate-800 dark:text-slate-100">{t("teachers.form.subject_specialisms", { defaultValue: "Subject specialisms" })}</p>
            {subjects.length ? (
              <div className="flex flex-wrap gap-2">
                {subjects.map((subject) => (
                  <label key={String(subject.id)} className={`cursor-pointer rounded-full border px-3 py-2 text-sm font-medium ${values.subjectIds.includes(String(subject.id)) ? "border-indigo-400 bg-indigo-50 text-indigo-800 dark:bg-indigo-400/10 dark:text-indigo-100" : "border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-slate-950/40 dark:text-slate-200"}`}>
                    <input className="sr-only" type="checkbox" checked={values.subjectIds.includes(String(subject.id))} onChange={() => toggleSubject(String(subject.id))} />
                    {subject.name}
                  </label>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">{t("teachers.form.no_subjects", { defaultValue: "Create subjects first to select specialisms." })}</p>}
          </div>
        ) : null}
      </section>

      {allowSharing ? (
        <section className={sectionClass}>
          <div className="mb-5 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-300">
              <FaShareAlt aria-hidden="true" />
            </div>
            <div>
              <h2 className={sectionTitleClass}>
                {t("teachers.form.privacy_publication", {
                  defaultValue: "Privacy and publication",
                })}
              </h2>
              <p className={sectionDescriptionClass}>
                {t("teachers.form.privacy_publication_description", {
                  defaultValue:
                    "Choose which contact details may be shown to others.",
                })}
              </p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            {sharingFields.map((field) => {
              const enabled = Boolean(values[field.name]);
              return (
                <button
                  type="button"
                  key={field.name}
                  role="switch"
                  aria-checked={enabled}
                  onClick={() => setValue(field.name, !enabled)}
                  className={`flex min-h-[112px] flex-col justify-between rounded-2xl border p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950 ${
                    enabled
                      ? "border-indigo-300 bg-indigo-50 text-indigo-950 dark:border-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-100"
                      : "border-slate-200 bg-white text-slate-800 hover:border-indigo-200 hover:bg-indigo-50/40 dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-100"
                  }`}
                >
                  <span className="flex w-full items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold">
                      {field.icon}
                      {t(field.key, { defaultValue: field.defaultValue })}
                    </span>
                    <span
                      aria-hidden="true"
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                        enabled
                          ? "bg-indigo-500"
                          : "bg-slate-300 dark:bg-slate-700"
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                          enabled ? "translate-x-5" : "translate-x-1"
                        }`}
                      />
                    </span>
                  </span>
                  <span className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {t(field.descriptionKey, {
                      defaultValue: field.descriptionDefault,
                    })}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <div className="sticky bottom-4 z-20 flex flex-wrap items-center justify-end gap-3 rounded-2xl border border-slate-200/80 bg-white/95 p-3 shadow-lg backdrop-blur dark:border-white/10 dark:bg-slate-950/90">
        <ActionButton tone="secondary" onClick={onCancel} disabled={submitting}>
          {t("common.cancel", { defaultValue: "Cancel" })}
        </ActionButton>
        <ActionButton type="submit" disabled={submitting}>
          {submitting
            ? t("common.saving", { defaultValue: "Saving..." })
            : submitLabel}
        </ActionButton>
      </div>
    </form>
  );
};

export default TeacherForm;
